import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Modal } from 'shared/ui/Modal';
import { Button } from 'shared/ui/Button';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { InvoiceItem, InvoiceItemType } from 'entities/Invoice';
import { Select } from 'shared/ui/Select';
import { InputNumber } from 'shared/ui/InputNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { POSITIVE_INTEGER_REGEX, SIX_DECIMALS_FLOAT_REGEX } from 'shared/utils/regex';
import { TextArea } from 'shared/ui/TextArea';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetVatRatesQuery } from 'entities/VatRate';

interface InoviceItemModalProps {
  isOpened: boolean;
  onClose: () => void;
  onSuccess: (value: Partial<InvoiceItem>) => void;
  invoiceItem?: Nullable<Partial<InvoiceItem>>;
}

export const InoviceItemModal: FC<InoviceItemModalProps> = memo((props) => {
  const { isOpened, onClose, onSuccess, invoiceItem } = props;

  const { t } = useAppTranslation(['contracts', 'common']);
  const [form] = Form.useForm();
  const currencySymbol = useGetCurrencySymbol();
  const { data: vatRates } = useGetVatRatesQuery();

  useEffect(() => {
    if (invoiceItem) {
      form.setFieldsValue(invoiceItem);
    } else {
      form.resetFields();
    }
  }, [invoiceItem, form]);

  const handleAdd = useCallback(
    (values: Partial<InvoiceItem>) => {
      const totalPrice = roundNumber(
        (values.initialPrice || 0) *
          (values.quantity || 0) *
          (1 - (values.discountPercent || 0) / 100) *
          (1 + (values.vatRatePercent || 0) / 100),
      );

      onSuccess({ ...values, totalPrice });
      onClose();
      form.resetFields();
    },
    [form, onClose, onSuccess],
  );

  const invoiceItemTypeOptions = Object.values(InvoiceItemType).map((option) => ({
    label: t(option, { ns: 'common' }),
    value: option,
  }));

  const vatRateOptions = useMemo(() => {
    const uniquePercents = Array.from(new Set(vatRates?.map((v) => v.ratePercent) || []));

    uniquePercents.sort((a, b) => b - a);

    return uniquePercents.map((percent) => ({
      label: `${percent}%`,
      value: percent,
    }));
  }, [vatRates]);

  return (
    <Modal isOpen={isOpened} onClose={onClose} width={650}>
      <div className="flex flex-col gap-6 px-6">
        <div className="text-xl font-semibold">{invoiceItem ? t('Edit Invoice Item') : t('Add Invoice Item')}</div>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ quantity: 1, discountPercent: 0, vatRatePercent: vatRateOptions[0]?.value }}
          onFinish={handleAdd}
        >
          <Form.Item name="invoiceItemType" label={t('Type')} rules={[{ required: true, message: t('Please select type') }]}>
            <Select placeholder={t('Select type')} options={invoiceItemTypeOptions} bordered size="small" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="initialPrice"
              label={t('Initial Price')}
              rules={[
                { required: true, message: t('Enter price') },
                {
                  pattern: SIX_DECIMALS_FLOAT_REGEX,
                  message: t('Maximum 6 digits after decimal point', { ns: 'common' }),
                },
              ]}
            >
              <InputNumber className="w-full" addonAfter={currencySymbol} bordered size="small" min={1} />
            </Form.Item>

            <Form.Item
              name="quantity"
              label={t('Quantity')}
              rules={[
                { required: true, message: t('Enter quantity') },
                { pattern: POSITIVE_INTEGER_REGEX, message: t('Please provide positive integer!') },
              ]}
            >
              <InputNumber className="w-full" bordered size="small" placeholder={t('Example: 1')} min={1} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="discountPercent" label={t('Discount')}>
              <InputNumber className="w-full" addonAfter="%" bordered size="small" min={0} max={99.99} />
            </Form.Item>

            <Form.Item name="vatRatePercent" label={t('VAT')}>
              <Select placeholder={t('VAT')} options={vatRateOptions} bordered size="small" allowClear />
            </Form.Item>
          </div>
          <Form.Item name="description" label={t('Description')}>
            <TextArea placeholder={t('Add your description...')} rows={2} bordered />
          </Form.Item>

          <div className="mt-4 flex justify-end gap-3">
            <Button theme="outlined" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button type="submit">{invoiceItem ? t('Save') : t('Add')}</Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
});
